/**
 * StatePanel — the reason the demo is credible rather than impressive.
 *
 * An LLM can *say* it feels betrayed. The claim this product makes is
 * that the state behind that is real, inspectable and rule-driven, and
 * this is the only place a stranger can check it in two minutes.
 *
 * ## The risk, and the answer that came from looking rather than reasoning
 *
 * `docs/02-specs/state-panel-contract.md` names the risk: there is a
 * version of this too honest to be attractive, that reads as a debug
 * view. It proposes the studio metaphor as the answer -- a trust value
 * as a ledger line on a score page rather than a number in a monospace
 * table.
 *
 * The direction loop (`docs/evidence/panel-v1/`) rendered three grammars
 * and found something more specific. **What keeps this off the debug
 * view is that the left column is sentences about what happened** --
 * "you were sharp with her", "you apologised" -- rather than the names
 * of fields. This exact layout with `trust_delta` and `rupture_floor`
 * down that column is a debug table in the same typeface. So the copy
 * lives in `StatePanel.copy.ts` and a test reads it for jargon, because
 * it turned out to be the load-bearing half.
 *
 * ## Why the movement is text and not animation
 *
 * Beat 4 is trust dropping, an apology, and trust *not* jumping back,
 * with the panel saying why. The movement is the argument, so the
 * contract requires it be legible with reduced motion on. That rules out
 * carrying it in a transition.
 *
 * So the argument is carried by things that do not move: the ledger rows
 * with their signed deltas, the word "falling" beside the balance, and
 * the ceiling drawn as a ruled line the balance sits under. Motion is
 * decoration on top of a panel that already reads correctly frozen. Turn
 * every animation off and beat 4 still lands.
 *
 * ## The ceiling
 *
 * `rupture_floor` is drawn as a rule across the ledger with the balance
 * beneath it. It is the one element taken from the channel-strip
 * direction, whose idea -- a value that visibly cannot pass a mark --
 * was the best answer to beat 4 of the three and whose execution was the
 * worst. Without it the beat reads as "the number did not go back up",
 * which reads as a bug rather than as the closing argument.
 *
 * Presentational, like its siblings on this page: no fetching, no socket.
 * The Rehearsal page owns the state and passes it down.
 */

import { type ReactElement, useEffect, useState } from "react";

import { statePanelCopy as copy } from "./StatePanel.copy";

export type MemoryTier = "held" | "settling" | "fading";

export interface MemoryTrace {
  readonly id: string;
  readonly text: string;
  /**
   * How clearly she holds it, or `null` when the wire did not say.
   *
   * Nullable rather than defaulted, because the only honest default is
   * no claim. See `rehearsal/retention.ts` for where the three tiers
   * come from and why an absent reading is not the bottom one.
   */
  readonly tier: MemoryTier | null;
}

/**
 * What the memory section knows, which is not the same as what it holds.
 *
 * A bare list cannot tell three situations apart, and the panel shipped
 * for months merging all three into one sentence that was only ever
 * true of the last:
 *
 * 1. **She was never asked.** No recall reached the panel, or it did and
 *    said retrieval had not run. The panel has no evidence and must
 *    claim nothing.
 * 2. **She looked and nothing bore on it.** Retrieval ran and matched
 *    nothing this turn. She may hold a great deal; none of it was about
 *    what was just said.
 * 3. **Nothing of hers is being kept.** The visitor has not opted in, so
 *    whatever is said goes when the socket does.
 *
 * `traces === null` is case 1; `remembered` splits 2 from 3. Both facts
 * are read off `server.recall` rather than worked out here, because the
 * session is the only thing that knows either of them.
 */
export interface MemoryRecall {
  /** `null` when retrieval did not run. `[]` when it ran and matched nothing. */
  readonly traces: readonly MemoryTrace[] | null;
  /** Whether anything this visitor says outlives the socket. */
  readonly remembered: boolean;
}

export interface TrustEntry {
  /** What happened, in words. Never a field name. */
  readonly id: string;
  readonly reason: string;
  readonly delta: number;
}

export interface TrustState {
  readonly voiceId: string;
  readonly name: string;
  /** 0-100. The contract forbids a field that only reads 0, 50 and 100. */
  readonly level: number;
  /** Shown by default: the most recent rows. See `olderEntries`. */
  readonly entries: readonly TrustEntry[];
  /**
   * Rows older than `entries`, kept rather than discarded so the "rest
   * of it" disclosure has something to reveal. #19: a panel that keeps
   * only four rows and shows no trace of the rest turns a long take's
   * history into an absence indistinguishable from nothing having
   * happened. Chronological, oldest first, same as `entries`.
   */
  readonly olderEntries: readonly TrustEntry[];
  /**
   * The ceiling the worst unrepaired rupture imposes, or null when there
   * is none. Beat 4 collapses without it.
   */
  readonly ceiling: number | null;
}

export interface GossipPath {
  readonly id: string;
  readonly content: string;
  readonly speaker: string | null;
  readonly hops: number;
  /** Per-hop confidence, oldest first. Two different values make the claim. */
  readonly confidences: readonly number[];
}

export interface WithheldNote {
  readonly id: string;
  readonly content: string;
  readonly reason: string;
}

export type PanelStatus = "live" | "loading" | "offline" | "failed";

export interface StatePanelProps {
  readonly trust: readonly TrustState[];
  /**
   * `null` until a recall frame arrives, then the three-state record.
   *
   * Not `readonly MemoryTrace[]`. That type could express "she recalled
   * nothing" and could not express *which* nothing, so every caller that
   * passed `[]` was making a claim it had no way to check, and the panel
   * rendered the strongest of the three sentences under all of them.
   */
  readonly memory: MemoryRecall | null;
  readonly paths: readonly GossipPath[];
  readonly withheld: readonly WithheldNote[];
  /**
   * Paths and withheld notes older than the ones shown by default. #19:
   * "the rest of it" for the two sections that truncate silently.
   * Optional because most callers (and every test written before #19)
   * have nothing older to report.
   */
  readonly olderPaths?: readonly GossipPath[];
  readonly olderWithheld?: readonly WithheldNote[];
  /**
   * Other characters holding something, when this one is not. Names
   * only; see `StatePanel.copy.ts` on why that is not a leak.
   */
  readonly withheldElsewhere?: readonly string[];
  readonly status?: PanelStatus;
}

const TIER_INK: Record<MemoryTier, string> = {
  held: "var(--paper-cream)",
  settling: "var(--paper-cream-dim)",
  fading: "var(--paper-cream-faint)",
};

const LABEL: React.CSSProperties = {
  fontSize: "0.656rem",
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "var(--paper-cream-faint)",
  marginBottom: "0.5rem",
};

/*
  Trust leads. In situ all four section labels carried identical weight,
  so the eye had nowhere to land first on a surface a stranger gets two
  minutes with -- and trust is the one the demo's closing beat turns on.
  Brighter and slightly larger is the whole of the difference; making it
  a different shape would start a second vocabulary.
*/
const LEAD_LABEL: React.CSSProperties = {
  ...LABEL,
  fontSize: "0.688rem",
  color: "var(--paper-cream-dim)",
};

const SECTION: React.CSSProperties = {
  paddingTop: "0.875rem",
  marginTop: "0.875rem",
  borderTop: "1px solid var(--ledger-line)",
};

/**
 * Whether this viewer has asked for less motion.
 *
 * Read live rather than once: a viewer who changes the system setting
 * mid-take should not have to reload to be believed.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/**
 * Which way this relationship last moved, read off the ledger.
 *
 * **Derived from the entries, not from render history.** The first
 * version of this compared the level against its own previous render,
 * which meant a panel that had only rendered once had no previous value
 * and reported "steady" for everybody. The in-situ loop caught it
 * immediately: Margo, nine points into a rupture, read "44 steady",
 * against a contract whose stated legibility target is the phrase
 * *"Trust 0.42, falling"*.
 *
 * The lesson is the more general one. The direction is a fact about the
 * data, and it was being inferred from a proxy for the data -- when
 * React happened to re-run. A visitor arriving mid-take, a remount, a
 * tab restored from cache: every one of those reports "steady" through
 * a rupture, and none of them looks broken enough to investigate.
 *
 * The last entry is the answer because the ledger is ordered and its
 * signs are the movement. No hook, no lifecycle, nothing to get out of
 * step with the number beside it.
 */
/** A signed delta as the ledger disclosure prints it: `+3`, `-9`, `0`. */
function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : `${delta}`;
}

function directionOf(entries: readonly TrustEntry[]): "rising" | "falling" | "steady" {
  const last = entries[entries.length - 1];
  if (last === undefined || last.delta === 0) return "steady";
  return last.delta > 0 ? "rising" : "falling";
}

function TrustLedger({ state }: { readonly state: TrustState }): ReactElement {
  const direction = directionOf(state.entries);
  const reduced = usePrefersReducedMotion();

  return (
    <div
      data-testid={`trust-${state.voiceId}`}
      style={{
        marginBottom: "0.75rem",
        /*
          Two relationships ran together in situ: Ellis's first row
          followed Margo's last line with no break, so at a glance the
          panel showed one long ledger rather than two people.
        */
        paddingBottom: "0.75rem",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "0.719rem",
        }}
      >
        <tbody>
          {state.entries.map((entry, index) => (
            <tr key={entry.id}>
              <td
                style={{
                  padding: "0.3rem 0",
                  borderBottom: "1px solid var(--ledger-line)",
                  color: "var(--paper-cream)",
                  width: "5.5rem",
                }}
              >
                {index === 0 ? state.name : ""}
              </td>
              <td
                style={{
                  padding: "0.3rem 0",
                  borderBottom: "1px solid var(--ledger-line)",
                  color: "var(--paper-cream-dim)",
                }}
              >
                {entry.reason}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/*
        #19: the signed number beside each reason is a price list -- the
        same brand lock that took the repair countdown off this panel
        ("telling people how many turns allows them to game it") one
        level down. The reason already says why the balance moved; a
        visitor who also knows the tariff is operating a state machine.
        Kept, not deleted: it moves to the foot disclosure, which is
        `disclosureLedger` below.
      */}
      {state.olderEntries.length > 0 && (
        <div
          data-testid={`more-${state.voiceId}`}
          style={{
            fontSize: "0.688rem",
            fontStyle: "italic",
            color: "var(--paper-cream-faint)",
            paddingTop: "0.3rem",
          }}
        >
          {copy.disclosure.moreHeld(state.olderEntries.length)}
        </div>
      )}

      {/*
        The balance leads. In situ the ceiling sat above it and the eye
        landed on a constraint before it had a value to attach the
        constraint to. A stranger wants "she is at 44" first and "and
        something is holding her there" second, which is also the order
        they will say it back in.
      */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontSize: "0.688rem",
          color: "var(--paper-cream-faint)",
          paddingTop: "0.45rem",
        }}
      >
        <span>{copy.trust.balance}</span>
        <span>
          {/*
            The number and the direction word are both plain text. This
            is the panel's argument and the contract requires it survive
            reduced motion, so a transition can never be what carries it.
          */}
          <span
            data-testid={`level-${state.voiceId}`}
            style={{
              fontFamily: "var(--font-plex-mono)",
              fontSize: "1.063rem",
              color: "var(--paper-cream)",
              transition: reduced ? "none" : "color 400ms ease-out",
            }}
          >
            {Math.round(state.level)}
          </span>
          <span
            data-testid={`direction-${state.voiceId}`}
            style={{ marginLeft: "0.4rem", color: "var(--paper-cream-dim)" }}
          >
            {copy.trust[direction]}
          </span>
        </span>
      </div>

      {/*
        The constraint, indented under the balance it constrains, in the
        dimmer brass rather than in the wrong-note red the first version
        used.

        It says she is holding back and does not say what would stop her.
        A countdown lived here -- "until 2 more good turns" -- and it came
        out on a brand lock: telling a visitor the exit condition lets
        them optimise against it, and a visitor who is optimising has
        stopped talking to a character and started operating a state
        machine. Beat 4 now has to land on the ceiling and on how she
        sounds, which is harder to build and better to watch. Red is this system's failure colour and in situ it read as
        a validation error; a rupture floor is not a failure, it is a
        limit, and brass is where limits live in this vocabulary. A
        plain grey rule was the other extreme and read as one more
        section divider.
      */}
      {state.ceiling !== null && (
        <div
          data-testid={`ceiling-${state.voiceId}`}
          style={{
            marginTop: "0.4rem",
            paddingLeft: "0.6rem",
            borderLeft: "2px solid var(--brass-tarnish)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "0.688rem",
              color: "var(--paper-cream-dim)",
            }}
          >
            <span>{copy.trust.ceiling}</span>
            <span style={{ fontFamily: "var(--font-plex-mono)" }}>
              {/*
                Rounded, like the balance two elements up. It was not, and
                the first live rupture rendered "she will not go past
                37.503619826873596" beside a balance reading "40" -- in
                beat 4's closing line, on the demo that is a link on a
                CV. The branch had never been taken in front of anyone:
                no rupture had fired live until the appraiser stopped
                silently dropping the hostile turn, so the one number on
                this surface that was not rounded was the one nobody had
                seen.
              */}
              {Math.round(state.ceiling)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function PathLine({ path }: { readonly path: GossipPath }): ReactElement {
  const first = path.confidences[0];
  const last = path.confidences[path.confidences.length - 1];
  const degraded = first !== undefined && last !== undefined && last < first;

  return (
    <div
      data-testid={`path-${path.id}`}
      style={{ marginBottom: "0.6rem", fontSize: "0.719rem", lineHeight: 1.6 }}
    >
      <div style={{ color: "var(--paper-cream)" }}>{path.content}</div>
      <div style={{ color: "var(--paper-cream-dim)" }}>
        {path.speaker === null ? (
          copy.graph.direct
        ) : (
          <>
            <span style={{ color: "var(--paper-cream)" }}>
              {copy.graph.heardFrom(path.speaker)}
            </span>{" "}
            <span style={{ color: "var(--paper-cream-faint)" }}>
              {copy.graph.hops(path.hops + 1)}
            </span>
            {". "}
            {degraded ? copy.graph.lessSure : copy.graph.asSure}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A withheld note, struck through with its reason.
 *
 * Extracted so the same row renders identically whether it is one of the
 * default four or one revealed by #19's disclosure -- a note that looks
 * different depending on which list it came from would read as two
 * features rather than one section with more in it.
 */
function WithheldRow({ note }: { readonly note: WithheldNote }): ReactElement {
  return (
    <div
      data-testid={`withheld-${note.id}`}
      style={{
        fontSize: "0.719rem",
        lineHeight: 1.6,
        marginBottom: "0.5rem",
        paddingLeft: "0.6rem",
        borderLeft: "1px solid var(--barline-strong)",
      }}
    >
      {/*
        Struck through, in the sense a score page strikes a bar it will
        not play. The reason underneath is the part #13 calls more
        convincing than any delivered item, so it is the brighter of the
        two.
      */}
      <div
        style={{
          color: "var(--paper-cream-faint)",
          textDecoration: "line-through",
          textDecorationColor: "var(--paper-cream-faint)",
        }}
      >
        {note.content}
      </div>
      <div style={{ color: "var(--paper-cream-dim)" }}>{note.reason}</div>
    </div>
  );
}

/**
 * How far something has travelled, past the point the default band
 * ("further off") stops distinguishing. #19: exact hop counts are
 * arithmetic in a sentence, so they live here rather than in `PathLine`.
 */
function FarPathLine({ path }: { readonly path: GossipPath }): ReactElement {
  return (
    <div
      data-testid={`disclosure-hops-${path.id}`}
      style={{ fontSize: "0.719rem", lineHeight: 1.5, marginBottom: "0.4rem" }}
    >
      <span style={{ color: "var(--paper-cream)" }}>{path.content}</span>{" "}
      <span style={{ color: "var(--paper-cream-faint)" }}>
        {copy.graph.hopsExact(path.hops + 1)}
      </span>
    </div>
  );
}

/**
 * The three things #19 keeps off the surface by default and puts one tap
 * away, together under a single native `<details>` at the foot of the
 * panel -- her register ("The rest of it"), one control rather than four.
 *
 * 1. Every ledger reason with the signed delta it cost or earned.
 * 2. The exact hop count for anything travelling further than "once
 *    removed" -- the default band stops distinguishing past that.
 * 3. The rows a section is currently holding but not showing, revealed
 *    in full rather than left as a bare count.
 *
 * Renders nothing when there is nothing to reveal, so a short take does
 * not carry an empty control.
 */
function RestOfIt({
  trust,
  paths,
  olderPaths,
  olderWithheld,
}: {
  readonly trust: readonly TrustState[];
  readonly paths: readonly GossipPath[];
  readonly olderPaths: readonly GossipPath[];
  readonly olderWithheld: readonly WithheldNote[];
}): ReactElement | null {
  const ledgerRows = trust.filter(
    (t) => t.entries.length > 0 || t.olderEntries.length > 0,
  );
  const farPaths = paths.filter((p) => p.hops + 1 > 2);

  const hasAnything =
    ledgerRows.length > 0 ||
    farPaths.length > 0 ||
    olderPaths.length > 0 ||
    olderWithheld.length > 0;
  if (!hasAnything) return null;

  return (
    <details data-testid="disclosure-rest" style={SECTION}>
      <summary
        style={{
          ...LABEL,
          marginBottom: 0,
          cursor: "pointer",
          listStyle: "revert",
        }}
      >
        {copy.disclosure.summary}
      </summary>

      <div style={{ paddingTop: "0.75rem" }}>
        {ledgerRows.length > 0 && (
          <div data-testid="disclosure-ledger" style={{ marginBottom: "0.75rem" }}>
            <div style={LABEL}>{copy.disclosure.ledgerLabel}</div>
            {ledgerRows.map((t) => (
              <div key={t.voiceId} style={{ marginBottom: "0.5rem" }}>
                <div style={{ color: "var(--paper-cream)", fontSize: "0.719rem" }}>
                  {t.name}
                </div>
                {[...t.olderEntries, ...t.entries].map((entry) => (
                  <div
                    key={entry.id}
                    data-testid={`disclosure-delta-${entry.id}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.75rem",
                      fontSize: "0.719rem",
                      color: "var(--paper-cream-dim)",
                    }}
                  >
                    <span>{entry.reason}</span>
                    <span
                      style={{
                        fontFamily: "var(--font-plex-mono)",
                        fontVariantNumeric: "tabular-nums",
                        color:
                          entry.delta < 0
                            ? "var(--dynamic-forte)"
                            : "var(--brass-cue)",
                      }}
                    >
                      {formatDelta(entry.delta)}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {farPaths.length > 0 && (
          <div data-testid="disclosure-hops" style={{ marginBottom: "0.75rem" }}>
            <div style={LABEL}>{copy.disclosure.graphLabel}</div>
            {farPaths.map((path) => (
              <FarPathLine key={path.id} path={path} />
            ))}
          </div>
        )}

        {olderPaths.length > 0 && (
          <div data-testid="disclosure-older-paths">
            {olderPaths.map((path) => (
              <PathLine key={path.id} path={path} />
            ))}
          </div>
        )}

        {olderWithheld.length > 0 && (
          <div data-testid="disclosure-older-withheld">
            {olderWithheld.map((note) => (
              <WithheldRow key={note.id} note={note} />
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

export function StatePanel({
  trust,
  memory,
  paths,
  withheld,
  olderPaths = [],
  olderWithheld = [],
  withheldElsewhere = [],
  status = "live",
}: StatePanelProps): ReactElement {
  return (
    <aside
      data-testid="state-panel"
      data-status={status}
      aria-label={copy.title}
      style={{
        background: "var(--stage-riser)",
        border: "1px solid var(--ledger-line)",
        borderRadius: "var(--radius)",
        boxShadow: "var(--shadow-lamp-warm)",
        overflow: "hidden",
        fontFamily: "var(--font-sohne-prose)",
      }}
    >
      {/*
        Title and status stack rather than sit side by side. Side by side,
        the failure message squeezed "The room" onto two lines and took
        over the header, so the longest and most alarming string in the
        component controlled the layout of the calmest one.
      */}
      <div
        style={{
          padding: "0.625rem 0.875rem",
          borderBottom: "1px solid var(--measure-line)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-sohne-display)",
            fontSize: "0.688rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--paper-cream-faint)",
          }}
        >
          {copy.title}
        </div>
        {status !== "live" && (
          <div
            data-testid="panel-status"
            role={status === "failed" ? "status" : undefined}
            style={{
              fontSize: "0.656rem",
              marginTop: "0.25rem",
              color:
                status === "failed"
                  ? "var(--dynamic-forte)"
                  : "var(--paper-cream-faint)",
            }}
          >
            {copy.states[status]}
          </div>
        )}
      </div>

      <div style={{ padding: "0.875rem" }}>
        <section>
          <div style={LEAD_LABEL}>{copy.trust.label}</div>
          {trust.length === 0 ? (
            <Empty text={copy.trust.empty} testId="trust-empty" waiting={status === "loading"} />
          ) : (
            trust.map((state) => <TrustLedger key={state.voiceId} state={state} />)
          )}
        </section>

        <section style={SECTION}>
          <div style={LABEL}>{copy.memory.label}</div>
          {memory === null || memory.traces === null ? (
            <Empty
              text={copy.memory.notAsked}
              testId="memory-not-asked"
              waiting={status === "loading"}
            />
          ) : memory.traces.length === 0 ? (
            <Empty
              text={memory.remembered ? copy.memory.nothingHeld : copy.memory.notKept}
              testId={memory.remembered ? "memory-nothing-held" : "memory-not-kept"}
              waiting={status === "loading"}
            />
          ) : (
            memory.traces.map((trace) => (
              <div
                key={trace.id}
                data-testid={`memory-${trace.id}`}
                data-tier={trace.tier ?? "unknown"}
                style={{
                  fontSize: "0.719rem",
                  lineHeight: 1.65,
                  /*
                    An unread tier takes the top ink rather than the
                    bottom. Dimming it would be a decay claim nobody made:
                    lighter ink here means "this is going", and saying that
                    about a memory whose retention never arrived is the
                    same fabrication as filing it under "Nearly gone".
                  */
                  color: TIER_INK[trace.tier ?? "held"],
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                }}
              >
                <span>{trace.text}</span>
                {/*
                  The tier in words as well as in ink. Lighter ink alone
                  is colour as the sole carrier of meaning, which fails
                  both WCAG and anyone reading this on a projector -- and
                  the take log one column over already solved the same
                  problem the same way.

                  Nothing is rendered when the wire sent no reading. The
                  span is dropped rather than filled with a dash or an
                  "unknown", because both of those are a word the visitor
                  then has to interpret, and the honest content is none.
                */}
                {trace.tier !== null && (
                  <span
                    style={{
                      color: "var(--paper-cream-faint)",
                      fontSize: "0.656rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {copy.memory.tiers[trace.tier]}
                  </span>
                )}
              </div>
            ))
          )}
        </section>

        <section style={SECTION}>
          <div style={LABEL}>{copy.graph.label}</div>
          {paths.length === 0 ? (
            <Empty text={copy.graph.empty} testId="paths-empty" waiting={status === "loading"} />
          ) : (
            paths.map((path) => <PathLine key={path.id} path={path} />)
          )}
          {olderPaths.length > 0 && (
            <div
              data-testid="more-paths"
              style={{
                fontSize: "0.688rem",
                fontStyle: "italic",
                color: "var(--paper-cream-faint)",
              }}
            >
              {copy.disclosure.moreHeld(olderPaths.length)}
            </div>
          )}
        </section>

        <section style={SECTION}>
          <div style={LABEL}>{copy.withheld.label}</div>
          {withheld.length === 0 ? (
            <Empty
              text={
                withheldElsewhere.length > 0
                  ? copy.withheld.elsewhere(withheldElsewhere)
                  : copy.withheld.empty
              }
              testId="withheld-empty"
              waiting={status === "loading"}
            />
          ) : (
            withheld.map((note) => <WithheldRow key={note.id} note={note} />)
          )}
          {olderWithheld.length > 0 && (
            <div
              data-testid="more-withheld"
              style={{
                fontSize: "0.688rem",
                fontStyle: "italic",
                color: "var(--paper-cream-faint)",
              }}
            >
              {copy.disclosure.moreHeld(olderWithheld.length)}
            </div>
          )}
        </section>

        <RestOfIt
          trust={trust}
          paths={paths}
          olderPaths={olderPaths}
          olderWithheld={olderWithheld}
        />
      </div>
    </aside>
  );
}

/**
 * The nothing-to-show line for one section.
 *
 * `waiting` is separate from `text` because they are different claims.
 * "She has held nothing back" says the panel looked and found none;
 * "Still listening" says it has not looked yet. Rendering the first
 * during a connection tells a visitor something false about a character
 * in the one surface built to be checked.
 */
function Empty({
  text,
  testId,
  waiting = false,
}: {
  readonly text: string;
  readonly testId: string;
  readonly waiting?: boolean;
}): ReactElement {
  return (
    <div
      data-testid={waiting ? "waiting" : testId}
      style={{
        fontSize: "0.719rem",
        color: "var(--paper-cream-faint)",
        fontStyle: "italic",
      }}
    >
      {waiting ? copy.waiting : text}
    </div>
  );
}

export default StatePanel;
